<template lang="pug">
.billing-list
    paginated-items(
        scope="base"
        type="billing"
        :loading="loading"
        :items="dashboardItems"
        :show-filters="showFilters"
        :filters="dashboardFilters"
        :variables="dashboardVariables"
        :all-columns="dashboardConfig.allColumns"
        :active-columns="dashboardConfig.activeColumns"
        :page-info="dashboardPageInfo"
        :sort="sort"
        @pagination-size-change="handleSizeChange"
        @pagination-current-change="handleCurrentChange"
        @show-table-columns="handleColsShow"
        @change-filter="handleFilterInputChange"
        @click="handleClick"
        @change-sort="handleSortInputChange"
    )
        div(slot="top")
            el-row.table-top(v-loading="loading" type="flex" justify="space-between")
                .left
                    h3 {{ $t('Billing Overview') }}
                .right.hide-on-empty
                    el-button(
                        plain
                        size="mini"
                        type="primary"
                        icon="el-icon-fa-filter fas"
                        @click="toggleFilterVisiblity"
                        :disabled="disable.showFilters || hasFilters"
                    )
                        span(v-if="showFilters") {{ $t('Hide filters') }}
                        span(v-else) {{ $t('Show filters') }}

        template(v-slot:currentPeriodStatus="{ value }")
            el-tag(
                v-if="value && value !== 'none'"
                :type="getStatusType(value)"
                size="mini"
            ) {{ getStatusLabel(value) }}
            span(v-else) -

        template(v-slot:interval="{ value }")
            span(v-if="value") {{ getIntervalLabel(value) }}
            span(v-else) -

        //- The period's invoice: an unpaid bank transfer is the one thing this
        //- list must not hide, so overdue reads red with its due date.
        template(v-slot:invoiceStatus="{ item }")
            el-tag(
                v-if="item.invoice_status"
                :type="getInvoiceStatusType(item)"
                size="mini"
            ) {{ getInvoiceStatusLabel(item) }}
            //- The bank's reason, so a failed debit is understood from the list.
            .invoice-reason(v-if="item.invoice_failure_code") {{ failureText(item.invoice_failure_code, item.invoice_failure_reason) }}
            span(v-if="!item.invoice_status") -

        template(v-slot:currentPeriodTotal="{ value }")
            span(v-if="value !== null && value !== undefined") {{ value | moneyFormat('EUR') }}
            span(v-else) -

        template(v-slot:currentPeriodStartsAt="{ value }")
            span(v-if="value") {{ value }}
            span(v-else) -

        template(v-slot:currentPeriodEndsAt="{ value }")
            span(v-if="value") {{ value }}
            span(v-else) -

        template(v-slot:interval:filter:options-template)
            component(
                v-for="item in options.billingPeriod.items"
                :key="item.key"
                :value="item.key"
                :label="$t(item.label)"
                :is="item.el"
            )
                template(v-if="item.options && item.options.length")
                    el-option(
                        v-for="option in item.options"
                        :key="option.key"
                        :value="option.key"
                        :label="$t(option.label)"
                    )
</template>

<i18n>
{
    "en": {
        "overdue since {date}": "overdue since {date}",
        "due {date}": "due {date}",
        "pending": "Pending",
        "open": "Open",
        "closed": "Closed",
        "invoiced": "Invoiced",
        "monthly": "Monthly",
        "quarterly": "Quarterly",
        "annually": "Annually",
        "annually-jan": "Annually January",
        "annually-feb": "Annually February",
        "annually-mar": "Annually March",
        "annually-apr": "Annually April",
        "annually-may": "Annually May",
        "annually-jun": "Annually June",
        "annually-jul": "Annually July",
        "annually-aug": "Annually August",
        "annually-sep": "Annually September",
        "annually-oct": "Annually October",
        "annually-nov": "Annually November",
        "annually-dec": "Annually December",
        "one-off": "One-off invoice"
    },
    "de": {
        "Billing Overview": "Abrechnungsübersicht",
        "Show filters": "Filter anzeigen",
        "Hide filters": "Filter verbergen",
        "Contract No.": "Vertragsnr.",
        "overdue since {date}": "überfällig seit {date}",
        "due {date}": "fällig {date}",
        "pending": "Ausstehend",
        "open": "Offen",
        "closed": "Abgeschlossen",
        "invoiced": "Fakturiert",
        "monthly": "Monatlich",
        "quarterly": "Quartalsweise",
        "annually": "Jährlich",
        "annually-jan": "Jährlich Januar",
        "annually-feb": "Jährlich Februar",
        "annually-mar": "Jährlich März",
        "annually-apr": "Jährlich April",
        "annually-may": "Jährlich Mai",
        "annually-jun": "Jährlich Juni",
        "annually-jul": "Jährlich Juli",
        "annually-aug": "Jährlich August",
        "annually-sep": "Jährlich September",
        "annually-oct": "Jährlich Oktober",
        "annually-nov": "Jährlich November",
        "annually-dec": "Jährlich Dezember",
        "one-off": "Einzelrechnung",
        "January": "Januar",
        "February": "Februar",
        "March": "März",
        "April": "April",
        "May": "Mai",
        "June": "Juni",
        "July": "Juli",
        "August": "August",
        "September": "September",
        "October": "Oktober",
        "November": "November",
        "December": "Dezember"
    }
}
</i18n>

<script>
import _ from 'lodash'
import { timestamp2date } from '@/filters'

import router from '@/router'
import pinia from '@/pinia'
import { useBillingStore, useSettingsStore, useSetupStore } from '@/stores'

import PaginatedItems from '@/components/PaginatedItems'

export default {
    name: 'BillingList',
    components: {
        PaginatedItems
    },
    data() {
        return {
            disable: {
                showFilters: false
            }
        }
    },
    computed: {
        billingStore() {
            return useBillingStore()
        },
        loading() {
            return this.billingStore.isLoading
        },
        dashboardItems() {
            return this.billingStore.dashboardItems
        },
        dashboardPageInfo() {
            return this.billingStore.dashboardPageInfo
        },
        dashboardConfig() {
            return this.billingStore.dashboardConfig
        },
        showFilters() {
            return this.hasFilters || _.get(this.dashboardConfig, 'showFilters', false)
        },
        hasFilters() {
            return !_.isEmpty(this.dashboardFilters)
        },
        dashboardFilters() {
            return _.get(this.billingStore.dashboard, 'filters', {})
        },
        dashboardVariables() {
            return _.get(this.billingStore.dashboard, 'variables', {})
        },
        sort() {
            var variables = this.dashboardVariables;
            var defaultSort = _.get(this.billingStore.dashboard, 'defaultSort') || { field: 'name', direction: 'asc' };
            var order = (variables.order !== undefined && variables.order !== '') ? variables.order : defaultSort.field;
            var direction = (variables.direction !== undefined && variables.direction !== '') ? variables.direction : defaultSort.direction;
            return { field: order, direction: direction };
        },
        options() {
            return useSettingsStore().options
        },
        setupStore() {
            return useSetupStore(pinia)
        }
    },
    methods: {
        formatDate(timestamp) {
            return timestamp2date(timestamp)
        },
        getIntervalLabel(key) {
            const translated = this.$t(key)
            // If translation returns the key itself, it means no translation was found
            return translated !== key ? translated : key
        },
        getStatusLabel(key) {
            const translated = this.$t(key)
            return translated !== key ? translated : key
        },
        getStatusType(status) {
            const types = {
                'pending': 'danger',
                'open': 'warning',
                'closed': 'info',
                'invoiced': 'success'
            }
            return types[status] || 'info'
        },
        getInvoiceStatusType(item) {
            if (item.invoice_overdue) return 'danger'
            const types = {
                'pending': 'warning',
                'paid': 'success',
                'failed': 'danger',
                'disputed': 'danger',
                'refunded': 'warning',
                'draft': 'info',
                'cancelled': 'info'
            }
            return types[item.invoice_status] || 'info'
        },
        paymentReason(code) {
            const key = 'payment_reason.' + code
            const label = this.$t(key)
            return label === key ? code : label
        },
        // The bank's reason in words where we know the code, Stripe's own
        // message otherwise (an API error such as resource_missing has none).
        failureText(code, reason) {
            const known = code && this.paymentReason(code) !== code
            return known ? this.paymentReason(code) : (reason || code)
        },
        getInvoiceStatusLabel(item) {
            if (item.invoice_overdue) {
                return this.$t('overdue since {date}', { date: this.formatDate(item.invoice_due_at) })
            }
            if (item.invoice_status === 'pending' && item.invoice_due_at) {
                return this.$t('due {date}', { date: this.formatDate(item.invoice_due_at) })
            }
            return this.$t('invoice_status.' + item.invoice_status)
        },
        handleClick(item) {
            const cmsId = _.get(item, 'cms.id')
            if (cmsId) {
                router.push({ name: 'system-billing-detail', params: { id: cmsId } })
            }
        },
        handleSizeChange(itemsPerPage) {
            this.defineItemsPerPage(itemsPerPage)
            this.defineCurrentPage(1)
            this.refetch()
        },
        handleCurrentChange(currentPage) {
            this.defineCurrentPage(currentPage)
            this.refetch()
        },
        handleColsShow(visibleColumns) {
            this.defineActiveColumns(visibleColumns)
            this.saveActiveColumnKeys({
                scope: 'base',
                key: 'billing',
                data: visibleColumns.map(key => ({ key }))
            })
        },
        handleFilterInputChange(key, value, debounce = true) {
            this.defineFilterValue({ key, value })
            if (debounce) {
                this.debouncedRefetch()
            } else {
                this.refetch()
            }
        },
        handleSortInputChange(key, value) {
            this.defineSorting({ key, value })
            this.refetch()
        },
        toggleFilterVisiblity() {
            const visible = this.showFilters

            let value = false
            if (!visible) value = true

            this.disable.showFilters = true
            this.billingStore.defineFilterVisibility(value)
            this.saveFilterVisibility({ scope: 'base', key: 'billing', value })
                .finally(() => this.disable.showFilters = false)
        },
        refetch() {
            this.billingStore.fetchDashboard()
        },
        debouncedRefetch: _.debounce(function() {
            this.refetch()
        }, useSetupStore(pinia).debounce || 300),
        defineActiveColumns(columns) {
            this.billingStore.defineActiveColumns(columns)
        },
        defineItemsPerPage(value) {
            this.billingStore.defineItemsPerPage(value)
        },
        defineCurrentPage(value) {
            this.billingStore.defineCurrentPage(value)
        },
        defineFilterValue(payload) {
            this.billingStore.defineFilterValue(payload)
        },
        defineSorting(payload) {
            this.billingStore.defineSorting(payload)
        },
        saveFilterVisibility(payload) {
            return this.setupStore.saveFilterVisibility(payload)
        },
        saveActiveColumnKeys(payload) {
            return this.setupStore.saveActiveColumnKeys(payload)
        }
    },
    mounted() {
        this.billingStore.fetchDashboard()
    }
}
</script>

<style lang="sass" scoped>
.billing-list
    h3
        margin: 0
        font-weight: 500

    .invoice-reason
        margin-top: 2px
        font-size: 0.75rem
        line-height: 1.4
        color: #909399
</style>
